import { TypeInitProps, TypeRequest } from '../../../types';
import { encryptParams } from '../../../utils/index';
import { WEB_AFF_URL } from '../../../utils/urls';
export default class InviteAPI {
  private request: any;
  constructor(props: TypeInitProps & { request: TypeRequest }) {
    const { request } = props;
    this.request = request;
  }
  /**
   * @param {string} mobile - the mobile used to invite
   * @param {number} deputy_identity_id - character of invitation authorization
   * @param {string} invited_name - the name of the invited user
   * @param {string} next_url - the redirect url after accepting invitation
   * @param {string} link - landing page of invitation
   * @param {string} role_keys - keys of role, joined by ','
   * @param {string} permissions - joined by ','
   * @return {Promise} invite result
   */
  mobileInvite = (params: {
    mobile: string;
    deputy_identity_id: number;
    invited_name: string;
    next_url: string;
    link?: string;
    role_keys?: string;
    permissions?: string;
    extra_params?: object;
  }) => {
    const { extra_params = {}, ...props } = params;
    const data = encryptParams(
      {
        ...props,
        ...extra_params,
      },
      ['mobile']
    );
    return this.request({
      url: WEB_AFF_URL.MOBILE_INVITE,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} email - the email used to invite
   * @param {number} deputy_identity_id - character of invitation authorization
   * @param {string} invited_name - the name of the invited user
   * @param {string} next_url - the redirect url after accepting invitation
   * @param {string} link - landing page of invitation
   * @param {string} role_keys - keys of role, joined by ','
   * @param {string} permissions - joined by ','
   * @return {Promise} invite result
   */
  emailInvite = (params: {
    email: string;
    deputy_identity_id: number;
    invited_name: string;
    next_url: string;
    link?: string;
    role_keys?: string;
    permissions?: string;
    extra_params?: object;
  }) => {
    const { extra_params = {}, ...props } = params;
    const data = encryptParams(
      {
        ...props,
        ...extra_params,
      },
      ['email']
    );
    return this.request({
      url: WEB_AFF_URL.EMAIL_INVITE,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} email - the email used to invite
   * @param {number} deputy_identity_id - character of invitation authorization
   * @param {string} invited_name - the name of the invited user
   * @param {string} role_keys - keys of role, joined by ','
   * @param {string} permissions - joined by ','
   * @return {Promise} invite result
   */
  // larkInvite = (params: {
  //   email: string;
  //   deputy_identity_id: number;
  //   invited_name: string;
  //   role_keys?: string;
  //   permissions?: string;
  //   extra_params?: object;
  // }) => {
  //   const { extra_params = {}, ...props } = params;
  //   const data = encryptParams(
  //     {
  //       ...props,
  //       ...extra_params,
  //     },
  //     ['email']
  //   );
  //   return this.request({
  //     url: WEB_AFF_URL.LARK_INVITE,
  //     method: 'POST',
  //     data,
  //   });
  // };

  /**
   * @param {number} page - page number
   * @param {number} page_size - page size
   * @return {Promise} request result
   */
  getInvitationList = (params: {
    page: number;
    page_size?: number;
    extra_params?: object;
  }) => {
    const { page = 1, page_size = 10, extra_params = {} } = params;
    return new Promise((resolve, reject) => {
      this.request({
        url: `${WEB_AFF_URL.INVITE_LIST}${page}/`,
        method: 'GET',
        data: {
          page_size,
          ...extra_params,
        },
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {number} invite_id - the invitation id
   * @return {Promise} cancel result
   */
  cancelInvitation = (params: { invite_id: number; extra_params?: object }) => {
    const { invite_id = 0, extra_params = {} } = params;
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_AFF_URL.CANCEL_INVITE,
        method: 'POST',
        data: {
          invite_id,
          ...extra_params,
        },
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {number} invite_id - the invitation id
   * @param {string} next_url - the redirect url after accepting invitation
   * @return {Promise} resend result
   */
  resendInvitation = (params: {
    invite_id: number;
    next_url?: string;
    link?: string;
    extra_params?: object;
  }) => {
    const {
      invite_id = 0,
      next_url = '',
      link = '',
      extra_params = {},
    } = params;
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_AFF_URL.RESEND_INVITE,
        method: 'POST',
        data: {
          invite_id,
          next_url,
          link,
          ...extra_params,
        },
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {number} invite_id - the invitation id
   * @param {number} deputy_identity_id - character of invitation authorization
   * @param {string} deputy_name - inviter name
   * @return {Promise} update result
   */
  updateInvitation = (params: {
    invite_id: number;
    deputy_identity_id?: number;
    deputy_name?: string;
    extra_params?: object;
  }) => {
    const {
      invite_id = 0,
      deputy_identity_id,
      deputy_name,
      extra_params = {},
    } = params;
    const data = encryptParams(
      { invite_id, deputy_identity_id, deputy_name, ...extra_params },
      ['deputy_name']
    );
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_AFF_URL.UPDATE_INVITE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} invite_ticket - invitation ticket carried in the query of invitation link, only necessary when the user is not login
   * @return {Promise} check result
   */
  checkRegisterStatus = (params: {
    invite_ticket?: string;
    extra_params?: object;
  }) => {
    const { invite_ticket, extra_params = {} } = params;
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_AFF_URL.CHECK_INVITE_VALID,
        method: 'POST',
        data: {
          invite_ticket,
          ...extra_params,
        },
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} invite_ticket - invitation ticket carried in the query of invitation link, only necessary when the user is not login
   * @param {string} password - necessary when the user is new user
   * @return {Promise} accept result
   */
  acceptInvitation = (params: {
    invite_ticket: string;
    password?: string;
    extra?: string;
    extra_params?: object;
  }) => {
    const { invite_ticket, password, extra, extra_params = {} } = params;
    const data = encryptParams(
      { invite_ticket, password, extra, ...extra_params },
      ['password', 'extra']
    );
    return this.request({
      url: WEB_AFF_URL.ACCEPT_INVITE,
      method: 'POST',
      data,
    });
  };
}
